/**
 * Éclat A/B testing — tracking module (customeclat).
 *
 * Loaded with `defer` from layout/theme.liquid. Depends on window.EclatAB
 * (snippets/eclat-ab.liquid). Does nothing for bots, in the theme editor,
 * when cookies are blocked, or while the Worker endpoint is not configured.
 *
 * Events (all anonymous, session-deduplicated client-side):
 *   exposure     – visitor assigned + slideshow with the test rendered
 *   impression   – assigned slide viewable (IAB: >=50% or >=50% of viewport
 *                  height, for 1s, while it is the carousel's selected slide)
 *   click        – click on the assigned slide / its CTA (mute buttons excluded)
 *   dest_view    – banner destination page opened after a click
 *   add_to_cart  – cart item count increased within the attribution window
 *   vitals       – LCP/CLS of a page that showed a test slide
 *
 * Order revenue is attributed server-side via the `_eab` cart attribute
 * (stamped here) and the orders/create webhook in eclat-ab-backend.
 *
 * When EclatAB.requireConsent is true, events are queued and sent only
 * after the visitor grants analytics consent (Shopify Customer Privacy API);
 * assignment itself is unaffected.
 */
(function () {
  'use strict';

  var AB = window.EclatAB;
  if (!AB || AB.bot || !AB.vid) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var ENDPOINT = null;
  if (AB.endpoint && AB.endpoint.indexOf('CHANGE-ME') === -1) {
    ENDPOINT = AB.endpoint.replace(/\/+$/, '') + '/v1/events';
  }

  var ATTR_WINDOW_MS = 7 * 24 * 3600 * 1000;
  var queue = AB._q || [];

  /* ---------- helpers ---------- */

  function sessionFlag(key) {
    try { return sessionStorage.getItem('_eab_' + key) === '1'; } catch (e) { return false; }
  }
  function setSessionFlag(key) {
    try { sessionStorage.setItem('_eab_' + key, '1'); } catch (e) {}
  }

  function attrMap() {
    try {
      var m = JSON.parse(localStorage.getItem('_eab_attr') || '{}');
      var out = {};
      for (var k in m) {
        if (m[k] && Date.now() - m[k].t < ATTR_WINDOW_MS) out[k] = m[k];
      }
      return out;
    } catch (e) { return {}; }
  }

  function consentOk() {
    if (!AB.requireConsent) return true;
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && cp.analyticsProcessingAllowed && cp.analyticsProcessingAllowed());
    } catch (e) { return false; }
  }

  /* ---------- transport ---------- */

  var flushTimer = null;

  function push(type, test, variant, meta) {
    queue.push({ t: type, test: test, variant: variant, ts: Date.now(), meta: meta || {} });
    if (queue.length > 100) queue.splice(0, queue.length - 100);
    scheduleFlush();
  }

  function scheduleFlush() {
    if (flushTimer || !ENDPOINT) return;
    flushTimer = setTimeout(flush, 5000);
  }

  function flush() {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    if (!ENDPOINT || !queue.length || !consentOk()) return;
    while (queue.length) {
      var events = queue.splice(0, 20);
      var payload = JSON.stringify({
        v: 1,
        vid: AB.vid,
        sid: AB.sid,
        path: location.pathname,
        dev: window.matchMedia && matchMedia('(max-width: 699px)').matches ? 'm' : 'd',
        lang: document.documentElement.lang || '',
        events: events
      });
      var sent = false;
      try {
        if (navigator.sendBeacon) {
          sent = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain' }));
        }
      } catch (e) {}
      if (!sent) {
        try {
          fetch(ENDPOINT, {
            method: 'POST',
            body: payload,
            keepalive: true,
            mode: 'cors',
            headers: { 'Content-Type': 'text/plain' }
          }).catch(function () {});
        } catch (e) {}
      }
    }
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
  // consent granted later in the session → release the queued events
  document.addEventListener('visitorConsentCollected', function () {
    if (queue.length) scheduleFlush();
  });

  // public hooks for other Éclat modules (e.g. the promo popup)
  AB.track = function (type, test, variant, meta) { push(type, test, variant, meta); };
  AB.flush = flush;

  /* ---------- viewable impressions ---------- */

  var io = null;
  var dwellTimers = new Map(); // element -> timeout id (per-element: the same
                               // handle may render in more than one section)

  function fireImpression(el) {
    var test = el.getAttribute('data-ab-test');
    var variant = el.getAttribute('data-ab-variant');
    if (sessionFlag('imp_' + test)) return;
    if (!el.classList.contains('is-selected') || el.hasAttribute('hidden')) return;
    setSessionFlag('imp_' + test);
    push('impression', test, variant, {});
  }

  function startDwell(el) {
    var test = el.getAttribute('data-ab-test');
    if (!test || dwellTimers.has(el) || sessionFlag('imp_' + test)) return;
    dwellTimers.set(el, setTimeout(function () {
      dwellTimers.delete(el);
      fireImpression(el);
    }, 1000));
  }

  function cancelDwell(el) {
    var t = dwellTimers.get(el);
    if (t) {
      clearTimeout(t);
      dwellTimers.delete(el);
    }
  }

  // IAB viewability: >=50% of the element, or (for elements taller than the
  // viewport) the visible part covering >=50% of the viewport height.
  function isViewable(entry) {
    if (!entry.isIntersecting) return false;
    if (entry.intersectionRatio >= 0.5) return true;
    var rootH = (entry.rootBounds && entry.rootBounds.height) || window.innerHeight || 0;
    return rootH > 0 && entry.intersectionRect.height >= 0.5 * rootH;
  }

  function assignedSlides() {
    var out = [];
    var slides = document.querySelectorAll('[data-ab-test]');
    for (var i = 0; i < slides.length; i++) {
      var el = slides[i];
      var v = AB.get(el.getAttribute('data-ab-test')) || 'A';
      if (v === el.getAttribute('data-ab-variant')) out.push(el);
    }
    return out;
  }

  function initImpressions() {
    var slides = assignedSlides();
    if (!slides.length || !('IntersectionObserver' in window)) return;
    if (io) io.disconnect();
    dwellTimers.forEach(function (t) { clearTimeout(t); });
    dwellTimers.clear();
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (isViewable(entry)) startDwell(entry.target);
        else cancelDwell(entry.target);
      });
    }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5] });
    slides.forEach(function (el) { io.observe(el); });

    // Restart the dwell timer when the carousel settles on a test slide
    // (non-selected slides overlap the same box, so IO alone is not enough).
    // Note: carousel:settle does NOT bubble — listen on each carousel element.
    slides.forEach(function (el) {
      var carousel = el.closest('slideshow-carousel');
      if (!carousel || carousel.__eabSettleBound) return;
      carousel.__eabSettleBound = true;
      carousel.addEventListener('carousel:settle', function (e) {
        var cell = e.detail && e.detail.cell;
        if (cell && cell.hasAttribute && cell.hasAttribute('data-ab-test')) startDwell(cell);
      });
    });
  }

  /* ---------- clicks + destination handoff ---------- */

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest || e.defaultPrevented) return;
    // volume controls sit inside the slide but are not banner engagement
    if (e.target.closest('[data-action="mute"],[data-action="unmute"]')) return;
    var slide = e.target.closest('[data-ab-test]');
    if (!slide) return;
    var test = slide.getAttribute('data-ab-test');
    var variant = slide.getAttribute('data-ab-variant');
    if ((AB.get(test) || 'A') !== variant) return;

    var link = e.target.closest('a[href]') || (slide.tagName === 'A' ? slide : null);
    var href = link ? link.getAttribute('href') : '';

    if (!sessionFlag('clk_' + test)) {
      setSessionFlag('clk_' + test);
      push('click', test, variant, { href: href || '' });
    }

    if (href) {
      try {
        var dest = new URL(href, location.origin);
        if (dest.origin === location.origin) {
          localStorage.setItem('_eab_nav', JSON.stringify({
            h: test, v: variant, dest: dest.pathname, t: Date.now()
          }));
        }
      } catch (err) {}
    }
    flush();
  }, true);

  function checkDestView() {
    var nav = null;
    try { nav = JSON.parse(localStorage.getItem('_eab_nav') || 'null'); } catch (e) {}
    if (!nav) return;
    try { localStorage.removeItem('_eab_nav'); } catch (e) {}
    if (Date.now() - nav.t > 30 * 60000) return;
    if (location.pathname === nav.dest && !sessionFlag('dst_' + nav.h)) {
      setSessionFlag('dst_' + nav.h);
      push('dest_view', nav.h, nav.v, {});
    }
  }

  /* ---------- add-to-cart + cart attribute stamping ---------- */

  var lastCount = null;
  var countSeeded = false;
  try {
    var stored = sessionStorage.getItem('_eab_cartn');
    if (stored !== null && stored !== '') {
      lastCount = parseInt(stored, 10);
      countSeeded = !isNaN(lastCount);
    }
  } catch (e) {}
  if (!countSeeded && typeof AB.cartCount === 'number' && isFinite(AB.cartCount)) {
    // server-rendered cart count from the snippet — always present, same
    // source of truth as the theme's own cart badge
    lastCount = AB.cartCount;
    countSeeded = true;
  }
  if (!countSeeded) {
    var cc = document.querySelector('cart-count');
    if (cc) {
      lastCount = parseInt((cc.textContent || '').replace(/\D/g, ''), 10) || 0;
      countSeeded = true;
    }
    // no stored value, no server count, no cart-count element:
    // the first cart:change only seeds the counter (no delta judgment),
    // so a removal from a pre-existing cart cannot count as an ATC.
  }

  var stamping = false;
  function stampCart(attrs) {
    var parts = ['vid=' + AB.vid];
    Object.keys(attrs).sort().forEach(function (h) { parts.push(h + ':' + attrs[h].v); });
    var val = parts.join(';');
    var prev = null;
    try { prev = sessionStorage.getItem('_eab_stamp'); } catch (e) {}
    if (val === prev || stamping) return;
    stamping = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _eab: val } })
    }).then(function (r) {
      if (r.ok) { try { sessionStorage.setItem('_eab_stamp', val); } catch (e) {} }
    }).catch(function () {}).finally(function () { stamping = false; });
  }

  document.documentElement.addEventListener('cart:change', function (e) {
    var cart = e.detail && e.detail.cart;
    if (!cart || typeof cart.item_count !== 'number') return;
    var prev = lastCount;
    var seeded = countSeeded;
    lastCount = cart.item_count;
    countSeeded = true;
    try { sessionStorage.setItem('_eab_cartn', String(lastCount)); } catch (err) {}

    var attrs = attrMap();
    if (!Object.keys(attrs).length) return;

    if (seeded && cart.item_count > prev) {
      for (var h in attrs) {
        if (sessionFlag('atc_' + h)) continue;
        setSessionFlag('atc_' + h);
        push('add_to_cart', h, attrs[h].v, {});
      }
    }
    if (cart.item_count > 0) stampCart(attrs);
  });

  /* ---------- web vitals guardrail (pages showing a test slide) ---------- */

  function initVitals() {
    if (!('PerformanceObserver' in window)) return;
    if (!document.querySelector('[data-ab-test]')) return;
    var lcp = 0, cls = 0, sent = false;
    try {
      new PerformanceObserver(function (l) {
        var es = l.getEntries();
        if (es.length) lcp = es[es.length - 1].startTime;
      }).observe({ type: 'largest-contentful-paint', buffered: true });
      new PerformanceObserver(function (l) {
        l.getEntries().forEach(function (en) { if (!en.hadRecentInput) cls += en.value; });
      }).observe({ type: 'layout-shift', buffered: true });
    } catch (e) { return; }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'hidden' || sent) return;
      sent = true;
      var slides = assignedSlides();
      for (var i = 0; i < slides.length; i++) {
        push('vitals', slides[i].getAttribute('data-ab-test'), slides[i].getAttribute('data-ab-variant'), {
          lcp: Math.round(lcp),
          cls: Math.round(cls * 1000) / 1000
        });
      }
      // the transport's own visibilitychange handler ran before this one,
      // so flush explicitly or the vitals would sit in a throttled tab
      if (slides.length) flush();
    });
  }

  /* ---------- init ---------- */

  function init() {
    initImpressions();
    initVitals();
    checkDestView();
    if (queue.length) scheduleFlush();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor / Section Rendering API safety: re-scan after re-render.
  document.addEventListener('shopify:section:load', initImpressions);
})();
